import React, { useEffect, useRef, useState } from "react";
import { Modal } from "bootstrap";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";

const DISPLAY_DELAY = 50000; // 2 minutes

export default function FeaturedVehiclesModal() {
  const [vehicles, setVehicles] = useState([]);
  const [title, setTitle] = useState("Nos 3 véhicules du moment");
  const [subtitle, setSubtitle] = useState(
    "Une sélection spéciale faite pour vous"
  );
  const [readyToShow, setReadyToShow] = useState(false);
  const [loading, setLoading] = useState(true);

  const modalRef = useRef(null);
  const bsModalRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchFeaturedVehicles = async () => {
      try {
        setLoading(true);

        const sectionRef = doc(db, "homepage_sections", "featured_choices");
        const sectionSnap = await getDoc(sectionRef);

        if (!sectionSnap.exists()) {
          setVehicles([]);
          return;
        }

        const sectionData = sectionSnap.data();

        if (sectionData.isActive === false) {
          setVehicles([]);
          return;
        }

        setTitle(sectionData.title || "Nos 3 véhicules du moment");
        setSubtitle(
          sectionData.subtitle || "Une sélection spéciale faite pour vous"
        );

        const vehicleIds = Array.isArray(sectionData.vehicleIds)
          ? sectionData.vehicleIds.slice(0, 3)
          : [];

        if (vehicleIds.length === 0) {
          setVehicles([]);
          return;
        }

        const vehiclesSnap = await getDocs(collection(db, "vehicules_auto"));
        const allVehicles = vehiclesSnap.docs.map((item) => ({
          id: item.id,
          ...item.data(),
        }));

        const selectedVehicles = vehicleIds
          .map((id) => allVehicles.find((v) => v.id === id))
          .filter(Boolean);

        setVehicles(selectedVehicles);
      } catch (error) {
        console.error("Erreur chargement véhicules mis en avant :", error);
        setVehicles([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedVehicles();
  }, []);

  useEffect(() => {
    const alreadyClosed = sessionStorage.getItem("featuredVehiclesModalClosed");
    const alreadyShown = sessionStorage.getItem("featuredVehiclesModalShown");

    if (alreadyClosed === "true" || alreadyShown === "true") return;

    const timer = setTimeout(() => {
      setReadyToShow(true);
      sessionStorage.setItem("featuredVehiclesModalShown", "true");
    }, DISPLAY_DELAY);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const modalElement = modalRef.current;

    if (!readyToShow || loading || vehicles.length === 0 || !modalElement) return;

    const instance = Modal.getOrCreateInstance(modalElement, {
      backdrop: true,
      keyboard: true,
    });

    bsModalRef.current = instance;
    instance.show();

    const handleHidden = () => {
      sessionStorage.setItem("featuredVehiclesModalClosed", "true");
    };

    modalElement.addEventListener("hidden.bs.modal", handleHidden);

    return () => {
      modalElement.removeEventListener("hidden.bs.modal", handleHidden);
    };
  }, [readyToShow, loading, vehicles]);

  const formatPrice = (prix) => {
    const value = Number(prix);
    if (!value || value <= 0) return "Prix à la demande";
    return `${value.toLocaleString("fr-FR")} FCFA`;
  };

  const handleClose = () => {
    sessionStorage.setItem("featuredVehiclesModalClosed", "true");
    bsModalRef.current?.hide();
  };

  const handleVehicleClick = (vehicle) => {
    sessionStorage.setItem("featuredVehiclesModalClosed", "true");
    bsModalRef.current?.hide();

    setTimeout(() => {
      if (vehicle.slug) {
        navigate(`/vehicules/${vehicle.slug}`);
      }
    }, 250);
  };

  if (loading || vehicles.length === 0) return null;

  return (
    <>
      <div
        className="modal fade"
        id="featuredVehiclesModal"
        tabIndex="-1"
        aria-labelledby="featuredVehiclesModalLabel"
        aria-hidden="true"
        ref={modalRef}
      >
        <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content border-0 shadow-lg overflow-hidden">
            <div
              className="modal-header border-0 text-white"
              style={{
                background: "linear-gradient(135deg, #0C1E47 0%, #1d4ed8 100%)",
              }}
            >
              <div>
                <h4
                  className="modal-title fw-bold mb-1"
                  id="featuredVehiclesModalLabel"
                >
                  {title}
                </h4>
                <p className="mb-0 text-white-50">{subtitle}</p>
              </div>

              <button
                type="button"
                className="btn-close btn-close-white"
                aria-label="Fermer"
                onClick={handleClose}
              ></button>
            </div>

            <div className="modal-body bg-light">
              <div className="row g-4">
                {vehicles.map((v, index) => (
                  <div key={v.id} className="col-12 col-md-6 col-lg-4">
                    <div
                      className="card h-100 border-0 shadow-sm featured-marketing-card"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleVehicleClick(v)}
                    >
                      <div className="position-relative">
                        <img
                          src={v.imageUrls?.[0] || "/assets/default.jpg"}
                          alt={`${v.marque} ${v.modele}`}
                          className="card-img-top"
                          style={{ height: "240px", objectFit: "cover" }}
                        />
                        <span className="badge bg-warning text-dark position-absolute top-0 start-0 m-3">
                          Choix #{index + 1}
                        </span>
                      </div>

                      <div className="card-body">
                        <h5 className="fw-bold mb-2">
                          {v.marque} {v.modele}
                        </h5>

                        <p className="text-muted small mb-2">
                          {v.annee} • {v.ficheTechnique?.carburant || "—"} •{" "}
                          {v.ficheTechnique?.transmission || "—"}
                        </p>

                        <p className="fw-bold text-primary fs-5 mb-3">
                          {formatPrice(v.prix)}
                        </p>

                        <button
                          type="button"
                          className="btn btn-bridge btn-sm w-100"
                        >
                          Voir ce véhicule
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="text-center mt-4">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handleClose}
                >
                  Continuer ma visite
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .featured-marketing-card {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .featured-marketing-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 1rem 2rem rgba(0,0,0,.12) !important;
        }
      `}</style>
    </>
  );
}
